import { useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/funnel';
import { SampleSignup } from '@/components/SampleSignup';

interface NewsletterCTAProps {
  variant?: 'default' | 'compact';
  /** Where the signup happened — stored with the address. */
  source?: string;
}

/**
 * Newsletter capture. The full-width variant leads with the free sample pack,
 * since a concrete artefact converts better than a promise of future emails.
 */
export const NewsletterCTA = ({ variant = 'default', source = 'newsletter' }: NewsletterCTAProps) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const { t } = useLanguage();

  if (variant === 'default') {
    return <SampleSignup source={source} id={`sample-${source}`} />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || state === 'sending') return;
    setState('sending');
    try {
      const { data, error } = await supabase.functions.invoke('subscribe', {
        body: { email: email.trim(), source: `${source}-footer`, wantsSample: false },
      });
      if (error || (data as { error?: string })?.error) throw new Error('failed');
      track('newsletter_signup', { source });
      setState('done');
      setEmail('');
    } catch {
      setState('error');
    }
  };

  if (state === 'done') {
    return (
      <div>
        <p className="text-[10px] tracking-luxury uppercase text-primary-foreground/80 mb-2">
          Confirmed
        </p>
        <p className="text-sm text-primary-foreground/80 font-light leading-relaxed">
          You are on the list. One dispatch a month, nothing else.
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-[10px] tracking-luxury uppercase text-primary-foreground/80 mb-4">
        {t('newsletter.heading')}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-wrap gap-2">
        <label htmlFor="footer-newsletter-email" className="sr-only">
          {t('newsletter.heading')}
        </label>
        <input
          id="footer-newsletter-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('newsletter.placeholder')}
          className="flex-1 min-w-0 basis-40 px-3 py-2 bg-primary-foreground/10 border border-primary-foreground/30 text-primary-foreground text-sm placeholder:text-primary-foreground/70 focus:outline-none focus:border-primary-foreground/60 transition-colors duration-500"
        />
        <button
          type="submit"
          disabled={state === 'sending'}
          className="px-4 py-2 bg-accent text-accent-foreground text-sm tracking-luxury uppercase hover:bg-accent/90 transition-colors duration-500 disabled:opacity-60"
        >
          {state === 'sending' ? '…' : t('newsletter.cta')}
        </button>
      </form>
      {state === 'error' && (
        <p className="text-[10px] text-primary-foreground/70 mt-2 font-light">
          That did not go through. Try again in a moment.
        </p>
      )}
      <p className="text-[10px] text-primary-foreground/70 mt-2 font-light">
        {t('newsletter.privacy')}
      </p>
    </div>
  );
};
